    import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { MatDateRangePicker } from '@angular/material/datepicker';
import { of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { IFilterField, IFilterFieldDatePeriod, IFilterFieldDateRange, IFilterFieldSelect, IFilterFieldSimple, IFilterFieldAutocomplete, IOptionValue } from 'src/app/models/generic-datatable/generic-datatable.model';
import { CustomDateAdapter } from 'src/app/shared/providers/mat-date-adapter';

@Component({
    selector: 'app-generic-filter',
    templateUrl: './generic-filter.component.html',
    styleUrls: ['./generic-filter.component.scss'],
    providers: [
        {provide: DateAdapter, useClass: CustomDateAdapter},
    ],
})
export class GenericFilterComponent implements OnInit {
    @Input() fields: IFilterField[] = [];
    @Input() autocompleteSearchFn?: (path: string, query: string) => Observable<{options: IOptionValue[]}>;

    @Output() search = new EventEmitter<any>();
    @Output() clear = new EventEmitter<void>();

    private destroy$ = new Subject<void>();

    filterForm!: FormGroup;
    datePeriodRadiosForm: FormGroup = this.fb.group({});

    filteredOptionsMap: Record<string, Observable<any[]>> = {};

    autocompleteLoadingMap: { [key: string]: boolean } = {};
    autocompleteOptionsMap: { [key: string]: IOptionValue[] } = {};

    constructor(private fb: FormBuilder) { }

    ngOnInit(): void {
        if (this.fields.length === 0) {
            throw new Error('No se proporcionaron campos.');
        }

        this.buildForms();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    buildForms(): void {
        const controls: { [key: string]: FormControl } = {};
        const datePeriodRadiosControls: { [key: string]: FormControl } = {};

        this.fields.forEach(field => {
            if (field.type === 'daterange') {
                controls[field.nameStart] = this.fb.control('');
                controls[field.nameEnd] = this.fb.control('');
            } else if (field.type === 'dateperiod') {
                controls[field.nameStart] = this.fb.control('');
                controls[field.nameEnd] = this.fb.control('');

                datePeriodRadiosControls[field.name + '_periodType'] = this.fb.control(field.options[0]);
            } else {
                controls[field.name] = this.fb.control('');
            }

            if (field.type === 'select' && field.options && field.options.length >= 5) {
                this.filteredOptionsMap[field.name] = controls[field.name].valueChanges.pipe(
                    startWith(''),
                    map(value => this.filterOptions(value, field.options))
                );
            }

            if (field.type === 'autocomplete') {
                this.autocompleteLoadingMap[field.name] = false;
                this.autocompleteOptionsMap[field.name] = [];

                const path = field.options[0];

                controls[field.name].valueChanges.pipe(
                    tap(value => {
                        if (typeof value !== 'string' || value.trim().length < 3) {
                            this.autocompleteOptionsMap[field.name] = [];
                            this.autocompleteLoadingMap[field.name] = false;
                        } else {
                            this.autocompleteLoadingMap[field.name] = true;
                        }
                    }),
                    debounceTime(300),
                    switchMap(value => {
                        if (typeof value !== 'string' || value.trim().length < 3) {
                            return of([]);
                        }

                        return this.autocompleteSearchFn
                            ? this.autocompleteSearchFn(path, value).pipe(
                                map(response => response.options ?? []),
                                catchError(() => of([]))
                            )
                            : of([])
                    }),
                    tap(() => (this.autocompleteLoadingMap[field.name] = false)),
                    takeUntil(this.destroy$)
                ).subscribe(results => {
                    this.autocompleteOptionsMap[field.name] = results;
                });
            }
        });

        this.datePeriodRadiosForm = this.fb.group(datePeriodRadiosControls);
        this.filterForm = this.fb.group(controls);
    }

    onSearch(): void {
        const filters = this.cleanEmptyValues(this.filterForm.value);
        this.search.emit(filters);
    }

    onClear(): void {
        this.filterForm.reset();
        this.clear.emit();
    }

    private cleanEmptyValues(obj: any): any {
        return Object.keys(obj).reduce((acc, key) => {
            const value = obj[key];
            if (value !== null && value !== undefined && value !== '') {
                acc[key] = value;
            }
            return acc;
        }, {} as any);
    }

    clearControl(key: string): void {
        if (!this.filterForm.get([key])) return;

        this.filterForm.get([key])?.setValue('');
        this.filterForm.get([key])?.markAsPristine();
        this.filterForm.get([key])?.markAsUntouched();
    }

    isDateRangeField(field: IFilterField): field is IFilterFieldDateRange {
        return field.type === 'daterange';
    }

    isDatePeriodField(field: IFilterField): field is IFilterFieldDatePeriod {
        return field.type === 'dateperiod';
    }

    isSelectField(field: IFilterField): field is IFilterFieldSelect {
        return field.type === 'select';
    }

    isAutocompleteField(field: IFilterField): field is IFilterFieldAutocomplete {
        return field.type === 'autocomplete';
    }

    isSimpleField(field: IFilterField): field is IFilterFieldSimple {
        return ['text', 'number', 'boolean', 'rut', 'email', 'date'].includes(field.type);
    }

    private filterOptions(value: string, options: any[]): any[] {
        const filterValue = (value || '').toLowerCase();

        return options.filter(option =>
            option.displayName.toLowerCase().includes(filterValue)
        );
    }

    chosenMonthHandler(date: Date, picker: MatDateRangePicker<Date>, field: IFilterFieldDatePeriod) {
        const y = date.getFullYear();
        const m = date.getMonth();
        const start = new Date(y, m, 8);
        const end   = new Date(y, m + 1, 8);

        this.filterForm.get([field.nameStart])?.setValue(start);
        this.filterForm.get([field.nameEnd])?.setValue(end);
        picker.close();
    }

    chosenYearHandler(date: Date, picker: MatDateRangePicker<Date>, field: IFilterFieldDatePeriod) {
        const y = date.getFullYear();
        const start = new Date(y, 0, 1);
        const end   = new Date(y, 11, 31);

        this.filterForm.get([field.nameStart])?.setValue(start);
        this.filterForm.get([field.nameEnd])?.setValue(end);
        picker.close();
    }

    onPeriodTypeChange(field: IFilterFieldDatePeriod) {
        this.filterForm.get([field.nameStart])?.reset();
        this.filterForm.get([field.nameEnd])?.reset();
    }
}
